import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import FavoritesController from '@jwp/ott-common/src/controllers/FavoritesController';
import useBreakpoint from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import ReactGA from 'react-ga4';
import _ from 'lodash';

import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';

import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Favorites from '../../components/Favorites/Favorites';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';

import styles from './FavouritesPage.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const FavouritesPage = (): JSX.Element => {
  const favoritesController = getModule(FavoritesController);
  const [clearFavoritesOpen, setClearFavoritesOpen] = useState(false);
  const { accessModel, favoritesList } = useConfigStore(
    (s) => ({
      accessModel: s.accessModel,
      favoritesList: s.config.features?.favoritesList,
    }),
    shallow,
  );
  const navigate = useNavigate();

  const breakpoint = useBreakpoint();
  const { t } = useTranslation('user');

  const { user: customer, subscription, loading } = useAccountStore();
  const { isGoogleAnalytics } = useAppConfig();
  const isLoggedIn = !!customer;
  const userId = isLoggedIn ? customer.id : 'Guest user';
  const deviceType = useGADeviceType();
  const favorites = useFavoritesStore((state) => state.getPlaylist());

  useEffect(() => {
    if (isGoogleAnalytics) {
      sendGAEvent('screen_view', {
        firebase_screen_class: 'Favourites',
        firebase_screen: 'Favourites',
        device_type: deviceType,
        userid_no: userId,
      });
    }
  }, []);

  useEffect(() => {
    if (!isGoogleAnalytics) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'Down' : 'Up';
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      if (scrollTop > 0) {
        sendGAEvent('scroll', {
          scroll_direction: direction,
          userid_no: userId,
          device_type: deviceType,
        });
      }
    }, 1000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!loading && !customer) {
      navigate('/', { replace: true });
    }
  }, [navigate, customer, loading]);

  if (!customer) {
    return (
      <div className={styles.user}>
        <LoadingOverlay inline />
      </div>
    );
  }

  return (
    <div className={styles.favouriteContainer}>
      <Favorites playlist={favorites} onClearFavoritesClick={() => setClearFavoritesOpen(true)} accessModel={accessModel} hasSubscription={!!subscription} />
      <ConfirmationDialog
        open={clearFavoritesOpen}
        title={t('favorites.clear_favorites_title')}
        body={t('favorites.clear_favorites_body')}
        onConfirm={async () => {
          await favoritesController.clear();
          setClearFavoritesOpen(false);
        }}
        onClose={() => setClearFavoritesOpen(false)}
      />
    </div>
  );
};

export default FavouritesPage;
